import { Badge } from "@comps/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import { Period, PeriodTypeQuarter } from "@dto/period";
import { DEFAULT_PERIOD_FORMAT } from "@utils/constants";
import { TW_ChoiceEditionPopoverContent, TW_PeriodBadge, getBadgeStyle } from "@utils/twClasses";
import { containsPosition, getPeriodFormats, posToString } from "@utils/types";
import { memo, useEffect, useMemo, useState } from "react";
import { useIsMobile } from "@comps/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore } from "src/store";
import { PeriodCellProps } from "./PeriodCell";
import { PeriodQuarterAndYearEditionForm } from "./PeriodQuarterAndYearEditionForm";


export const PeriodQuarterCell = memo(function PeriodQuarterCell({ pathKey, field, readOnly, period, position, updatePeriod, isEditing, onClick, onEnterKeyPress, searchValue, setSearchValue, searchInputRef }: PeriodCellProps) {

    const [value, setValue] = useState<Period | undefined>(period);
    const wsColor = useModelStore(state => state.wsColor);
    const { t } = useTranslation();
    const focus = useDataStore(state => state.focus);
    const isMobile = useIsMobile();
    const selectedView = useModelStore(state => state.selectedView);

    useEffect(() => {
        if (period !== value) {
            setValue(period);
        }
    }, [period]);

    const badgeStyle = useMemo(() => {
        return getBadgeStyle(wsColor);
    }, [wsColor]);

    function onPeriodChange(period: Period | undefined) {
        setValue(period);
        updatePeriod(period);
        focus(position);
    }

    function setIsDialogOpen(isOpen: boolean) {
        if (!isOpen) {
            const currentFocusPos = useDataStore.getState().focusPos;
            if (!currentFocusPos || containsPosition(currentFocusPos, position)) {
                focus(position);
            }
        }
    }


    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    const periodFormatKey = (format?.get("periodFormat") as string) ?? DEFAULT_PERIOD_FORMAT;
    const periodFormat = getPeriodFormats(PeriodTypeQuarter)?.get(periodFormatKey) ?? DEFAULT_PERIOD_FORMAT;
   return (
        <div className={`w-full ${readOnly ? "cursor-not-allowed" : "cursor-pointer"}`} onClick={onClick}>
            <Popover open={isEditing} onOpenChange={setIsDialogOpen}>
                <PopoverTrigger className={`rounded-md px-1 outline-none focus:outline-none hover:outline-none focus:border-none ${readOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                    onKeyDown={onEnterKeyPress} tabIndex={0}>
                    {value &&
                        <Badge key={posToString(position) + "_badge_" + field._key} className={TW_PeriodBadge + (readOnly ? " cursor-not-allowed" : "")} style={badgeStyle} >
                            {value.toString(t, periodFormat, PeriodTypeQuarter)}
                        </Badge>}
                </PopoverTrigger>
                <PopoverContent className={TW_ChoiceEditionPopoverContent} side={isMobile ? "bottom" : "right"}>
                    <PeriodQuarterAndYearEditionForm field={field} value={value} updatePeriod={onPeriodChange} searchValue={searchValue} setSearchValue={setSearchValue} searchInputRef={searchInputRef} displaySearch={true} hasValidation={true} />
                </PopoverContent>
            </Popover>
        </div>
    )

}, (prevProps, nextProps) => {
    return prevProps.field === nextProps.field && prevProps.period === nextProps.period && prevProps.isEditing === nextProps.isEditing && prevProps.searchValue === nextProps.searchValue;
});